import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  invoicesApi,
  PendingInvoice,
  PublishedInvoice,
  InvoiceSyncHistoryItem,
} from '../lib/api';
import {
  FileText, Upload, CheckCircle, XCircle,
  MailCheck, Loader2, Clock, History,
  Settings, Info, FileUp, X
} from 'lucide-react';
import InvoiceEmailConfigModal from '../components/InvoiceEmailConfigModal';

// Resultado de procesamiento de un PDF
interface ProcessResult {
  filename: string;
  success: boolean;
  serie?: string;
  codigo?: string;
  order_id?: number;
  email_sent?: boolean;
  email_to?: string;
  error?: string;
  status: 'pending' | 'processing' | 'done';
}

export default function InvoicesPage() {
  const queryClient = useQueryClient();

  // UI State
  const [activeTab, setActiveTab] = useState<'pending' | 'published' | 'history'>('pending');
  const [showEmailConfig, setShowEmailConfig] = useState(false);

  // Upload and Process State
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [processResults, setProcessResults] = useState<ProcessResult[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processStartTime, setProcessStartTime] = useState<number | null>(null);
  const [currentProcessIndex, setCurrentProcessIndex] = useState(0);
  const [, setTick] = useState(0); // Force re-render for time estimate
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resultsListRef = useRef<HTMLDivElement>(null);

  // Concurrency limit for parallel processing
  const CONCURRENCY_LIMIT = 5;

  // Update time estimate every second while processing
  useEffect(() => {
    if (isProcessing && processStartTime) {
      const interval = setInterval(() => {
        setTick(t => t + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isProcessing, processStartTime]);

  // Auto-scroll to top of results list when processing changes
  useEffect(() => {
    if (isProcessing && resultsListRef.current) {
      resultsListRef.current.scrollTop = 0;
    }
  }, [processResults, isProcessing]);

  // Queries
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['invoiceStats'],
    queryFn: invoicesApi.getStats,
    refetchInterval: 30000,
  });

  const { data: pendingData, isLoading: pendingLoading } = useQuery({
    queryKey: ['pendingInvoices'],
    queryFn: () => invoicesApi.getPendingInvoices(),
    enabled: activeTab === 'pending',
  });

  const { data: publishedData, isLoading: publishedLoading } = useQuery({
    queryKey: ['publishedInvoices'],
    queryFn: () => invoicesApi.getPublishedInvoices(100),
    enabled: activeTab === 'published',
  });

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['invoiceHistory'],
    queryFn: () => invoicesApi.getHistory(50),
    enabled: activeTab === 'history',
  });

  const stats = statsData?.stats;

  // Regex para validar formato de nombre de factura
  const INVOICE_FILENAME_PATTERN = /^(?:Factura\s+)?(\d+)-(\d+)\.pdf$/i;
  const isValidFilename = (filename: string) => INVOICE_FILENAME_PATTERN.test(filename);

  // Process a single file and update results
  const processFile = async (file: File, index: number): Promise<void> => {
    // Mark as processing
    setProcessResults(prev => prev.map((r, idx) =>
      idx === index ? { ...r, status: 'processing' as const } : r
    ));

    try {
      const result = await invoicesApi.uploadAndProcessPdf(file);

      setProcessResults(prev => prev.map((r, idx) =>
        idx === index ? {
          ...r,
          success: result.success,
          serie: result.serie,
          codigo: result.codigo,
          order_id: result.order_id,
          email_sent: result.email_sent,
          email_to: result.email_to,
          error: result.error,
          status: 'done' as const
        } : r
      ));
    } catch (error: any) {
      let errorMessage = 'Error desconocido';
      if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
        errorMessage = 'Timeout - el servidor tardó demasiado';
      } else if (error?.response?.status === 500) {
        errorMessage = 'Error del servidor';
      } else if (error?.response?.status === 413) {
        errorMessage = 'Archivo demasiado grande';
      } else if (error?.message) {
        errorMessage = error.message;
      }

      console.error(`Error processing ${file.name}:`, error);

      setProcessResults(prev => prev.map((r, idx) =>
        idx === index ? {
          ...r,
          success: false,
          error: errorMessage,
          status: 'done' as const
        } : r
      ));
    }

    // Update processed count
    setCurrentProcessIndex(prev => prev + 1);
  };

  // Process files in parallel with concurrency limit
  const processFilesParallel = async (files: File[]) => {
    setIsProcessing(true);
    setProcessStartTime(Date.now());
    setCurrentProcessIndex(0);

    // Initialize results
    const initialResults: ProcessResult[] = files.map(f => ({
      filename: f.name,
      success: false,
      status: 'pending' as const
    }));
    setProcessResults(initialResults);

    // Process in batches with concurrency limit
    const queue = files.map((file, index) => ({ file, index }));
    const activePromises: Promise<void>[] = [];

    while (queue.length > 0 || activePromises.length > 0) {
      // Start new tasks up to concurrency limit
      while (activePromises.length < CONCURRENCY_LIMIT && queue.length > 0) {
        const item = queue.shift()!;
        const promise = processFile(item.file, item.index).then(() => {
          // Remove from active promises when done
          const idx = activePromises.indexOf(promise);
          if (idx > -1) activePromises.splice(idx, 1);
        });
        activePromises.push(promise);
      }

      // Wait for at least one to complete before continuing
      if (activePromises.length > 0) {
        await Promise.race(activePromises);
      }
    }

    setIsProcessing(false);
    setProcessStartTime(null);
    setCurrentProcessIndex(0);
    setSelectedFiles([]);

    // Refresh data
    queryClient.refetchQueries({ queryKey: ['invoiceStats'] });
    queryClient.refetchQueries({ queryKey: ['pendingInvoices'] });
    queryClient.refetchQueries({ queryKey: ['publishedInvoices'] });
    queryClient.refetchQueries({ queryKey: ['invoiceHistory'] });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const pdfFiles = files.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    setSelectedFiles(prev => [...prev, ...pdfFiles]);
    setProcessResults([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    const pdfFiles = files.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    setSelectedFiles(prev => [...prev, ...pdfFiles]);
    setProcessResults([]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearAllFiles = () => {
    setSelectedFiles([]);
    setProcessResults([]);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const validFiles = selectedFiles.filter(f => isValidFilename(f.name));
  const invalidFiles = selectedFiles.filter(f => !isValidFilename(f.name));

  // Stats Cards
  const renderStats = () => {
    if (statsLoading) {
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg border p-4 animate-pulse shadow-sm">
              <div className="h-4 bg-slate-200 rounded w-20 mb-2"></div>
              <div className="h-8 bg-slate-200 rounded w-16"></div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border p-4 shadow-sm">
          <div className="text-slate-500 text-sm">Ano {stats?.year}</div>
          <div className="text-2xl font-bold text-gray-900">{stats?.total_invoices || 0}</div>
          <div className="text-slate-400 text-xs">Total Facturas</div>
        </div>

        <div className="bg-white rounded-lg border p-4 shadow-sm">
          <div className="flex items-center gap-2 text-success-600 text-sm">
            <CheckCircle size={14} />
            Publicadas
          </div>
          <div className="text-2xl font-bold text-success-600">{stats?.published || 0}</div>
          <div className="text-slate-400 text-xs">{stats?.publish_rate || 0}% completado</div>
        </div>

        <div className="bg-white rounded-lg border p-4 shadow-sm">
          <div className="flex items-center gap-2 text-yellow-600 text-sm">
            <Clock size={14} />
            Pendientes
          </div>
          <div className="text-2xl font-bold text-yellow-600">{stats?.pending || 0}</div>
          <div className="text-slate-400 text-xs">Sin PDF</div>
        </div>

        <div className="bg-white rounded-lg border p-4 shadow-sm">
          <div className="flex items-center gap-2 text-purple-600 text-sm">
            <MailCheck size={14} />
            Emails
          </div>
          <div className="text-2xl font-bold text-purple-600">{stats?.emails_sent || 0}</div>
          <div className="text-slate-400 text-xs">Enviados</div>
        </div>
      </div>
    );
  };

  // Upload and Process Section
  const renderUploadSection = () => (
    <div className="bg-white rounded-lg border p-4 mb-6 shadow-sm">
      <h3 className="text-gray-900 font-medium mb-3 flex items-center gap-2">
        <FileUp size={18} className="text-primary-600" />
        Subir y Publicar Facturas PDF
      </h3>

      <p className="text-slate-500 text-sm mb-4">
        Al subir los PDFs se publicaran automaticamente en la web y se enviara email al cliente.
      </p>

      {/* Drop Zone */}
      <div
        onClick={() => !isProcessing && fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isProcessing
            ? 'border-slate-200 bg-slate-50 cursor-not-allowed'
            : isDragging
              ? 'border-blue-400 bg-blue-50 cursor-pointer'
              : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50 cursor-pointer'
        }`}
      >
        <Upload size={32} className="mx-auto text-slate-400 mb-2" />
        <p className="text-slate-600 text-sm">
          {isProcessing ? 'Procesando archivos...' : 'Arrastra archivos PDF aqui o haz clic para seleccionar'}
        </p>
        <p className="text-slate-400 text-xs mt-1">
          Formato: SERIE-CODIGO.pdf (ej: 1-12345.pdf)
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          multiple
          onChange={handleFileSelect}
          disabled={isProcessing}
          className="hidden"
        />
      </div>

      {/* Selected Files List */}
      {selectedFiles.length > 0 && !isProcessing && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-700 text-sm font-medium">
              Archivos seleccionados ({selectedFiles.length})
            </span>
            <button
              onClick={clearAllFiles}
              className="text-slate-500 hover:text-danger-600 text-sm"
            >
              Limpiar todo
            </button>
          </div>

          <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-2 bg-slate-50">
            {selectedFiles.map((file, index) => {
              const isValid = isValidFilename(file.name);
              return (
                <div
                  key={index}
                  className={`flex items-center justify-between px-2 py-1.5 rounded ${
                    isValid ? 'bg-white' : 'bg-red-50'
                  }`}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isValid ? (
                      <CheckCircle size={14} className="text-success-500 flex-shrink-0" />
                    ) : (
                      <XCircle size={14} className="text-danger-500 flex-shrink-0" />
                    )}
                    <span className={`text-sm truncate ${isValid ? 'text-gray-700' : 'text-danger-600'}`}>
                      {file.name}
                    </span>
                    <span className="text-slate-400 text-xs flex-shrink-0">
                      ({formatFileSize(file.size)})
                    </span>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="text-slate-400 hover:text-danger-500 ml-2 flex-shrink-0"
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>

          {invalidFiles.length > 0 && (
            <p className="text-danger-500 text-xs mt-2">
              {invalidFiles.length} archivo(s) con nombre invalido. Deben seguir el formato SERIE-CODIGO.pdf
            </p>
          )}

          {/* Process Button */}
          <div className="mt-3 flex items-center justify-between">
            <span className="text-slate-500 text-sm">
              {validFiles.length} archivo(s) valido(s) para procesar
            </span>
            <button
              onClick={() => processFilesParallel(selectedFiles)}
              disabled={selectedFiles.length === 0}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2 text-sm"
            >
              <Upload size={16} />
              Subir y Publicar ({selectedFiles.length})
            </button>
          </div>
        </div>
      )}

      {/* Processing Results */}
      {processResults.length > 0 && (
        <div className="mt-4 space-y-3">
          {/* Progress Bar - only show while processing */}
          {isProcessing && processStartTime && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-blue-900 font-medium flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  Procesando facturas...
                </span>
                <span className="text-blue-700 font-medium">
                  {currentProcessIndex} / {processResults.length}
                </span>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-blue-200 rounded-full h-3 mb-2">
                <div
                  className="bg-primary-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${(currentProcessIndex / processResults.length) * 100}%` }}
                />
              </div>

              {/* Stats and time estimate */}
              <div className="flex items-center justify-between text-sm">
                <div className="flex gap-4">
                  <span className="text-success-600">
                    {processResults.filter(r => r.success).length} ok
                  </span>
                  <span className="text-danger-600">
                    {processResults.filter(r => !r.success && r.status === 'done').length} errores
                  </span>
                </div>
                <div className="text-blue-700">
                  {(() => {
                    const elapsed = Date.now() - processStartTime;
                    const processed = processResults.filter(r => r.status === 'done').length;
                    if (processed > 0) {
                      const avgTime = elapsed / processed;
                      const remaining = processResults.length - processed;
                      const estimatedRemaining = Math.round((avgTime * remaining) / 1000);
                      if (estimatedRemaining > 60) {
                        const mins = Math.floor(estimatedRemaining / 60);
                        const secs = estimatedRemaining % 60;
                        return `~${mins}m ${secs}s restantes`;
                      }
                      return `~${estimatedRemaining}s restantes`;
                    }
                    return 'Calculando...';
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex items-center justify-between">
            <span className="text-gray-700 text-sm font-medium">
              Resultados {isProcessing ? '' : `(${processResults.length} archivos)`}
            </span>
            {!isProcessing && (
              <button
                onClick={() => setProcessResults([])}
                className="text-slate-500 hover:text-gray-700 text-sm"
              >
                Limpiar
              </button>
            )}
          </div>

          {/* Results list - stable order during processing to avoid React DOM sync issues */}
          <div ref={resultsListRef} className="max-h-64 overflow-y-auto space-y-1 border rounded-lg p-2 bg-slate-50">
            {(() => {
              // Only sort when NOT processing to avoid React removeChild errors
              // During processing, keep original order for DOM stability
              let displayResults = processResults;

              if (!isProcessing && processResults.length > 0) {
                // Sort only when done: processing first, then done (newest first), then pending
                const processing = processResults.filter(r => r.status === 'processing');
                const done = processResults.filter(r => r.status === 'done').reverse();
                const pending = processResults.filter(r => r.status === 'pending');
                displayResults = [...processing, ...done, ...pending];
              }

              return displayResults.map((result) => {
                return (
                  <div
                    key={result.filename}
                    className={`flex items-center justify-between px-3 py-2 rounded ${
                      result.status === 'processing' ? 'bg-blue-50 border border-blue-200' :
                      result.status === 'done' && result.success ? 'bg-green-50' :
                      result.status === 'done' && !result.success ? 'bg-red-50' :
                      'bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {result.status === 'pending' && (
                        <Clock size={14} className="text-slate-400 flex-shrink-0" />
                      )}
                      {result.status === 'processing' && (
                        <Loader2 size={14} className="text-primary-600 animate-spin flex-shrink-0" />
                      )}
                      {result.status === 'done' && result.success && (
                        <CheckCircle size={14} className="text-success-500 flex-shrink-0" />
                      )}
                      {result.status === 'done' && !result.success && (
                        <XCircle size={14} className="text-danger-500 flex-shrink-0" />
                      )}

                      <span className="text-sm font-medium text-gray-700 truncate">
                        {result.filename}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-xs flex-shrink-0 ml-2">
                      {result.status === 'processing' && (
                        <span className="text-primary-600">Procesando...</span>
                      )}
                      {result.status === 'done' && result.success && (
                        <>
                          <span className="text-success-600">Publicada</span>
                          {result.order_id && (
                            <span className="text-slate-500">#{result.order_id}</span>
                          )}
                          {result.email_sent && (
                            <span className="text-purple-600 flex items-center gap-1">
                              <MailCheck size={12} />
                              {result.email_to ? result.email_to.substring(0, 20) + '...' : 'Email'}
                            </span>
                          )}
                        </>
                      )}
                      {result.status === 'done' && !result.success && (
                        <span className="text-danger-600 max-w-xs truncate" title={result.error}>
                          {result.error}
                        </span>
                      )}
                    </div>
                  </div>
                );
              });
            })()}
          </div>

          {/* Summary - only show when done */}
          {!isProcessing && processResults.length > 0 && (
            <div className="flex items-center justify-center gap-4 pt-2 text-sm bg-slate-100 rounded-lg py-3">
              <span className="text-success-600 font-medium">
                {processResults.filter(r => r.success).length} publicadas
              </span>
              <span className="text-danger-600 font-medium">
                {processResults.filter(r => !r.success && r.status === 'done').length} errores
              </span>
              <span className="text-purple-600 font-medium">
                {processResults.filter(r => r.email_sent).length} emails enviados
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Pending Invoices (from DB)
  const renderPendingInvoices = () => {
    if (pendingLoading) {
      return <div className="text-slate-500 text-center py-8">Cargando facturas pendientes...</div>;
    }

    const invoices = pendingData?.invoices || [];

    if (invoices.length === 0) {
      return (
        <div className="text-center py-12 text-slate-500">
          <CheckCircle size={48} className="mx-auto mb-4 text-success-500 opacity-50" />
          <p>Todas las facturas estan publicadas</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-slate-500 text-sm border-b border-gray-200">
              <th className="pb-3 pl-4">Factura</th>
              <th className="pb-3">Pedido</th>
              <th className="pb-3">Fecha</th>
              <th className="pb-3 text-right pr-4">Total</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv: PendingInvoice, i: number) => (
              <tr key={i} className="border-b border-gray-100 hover:bg-slate-50">
                <td className="py-3 pl-4">
                  <span className="text-gray-900 font-medium">{inv.serie}-{inv.codigo}</span>
                </td>
                <td className="py-3">
                  <span className="text-primary-600">#{inv.order_id}</span>
                </td>
                <td className="py-3 text-slate-500 text-sm">
                  {inv.fecha ? new Date(inv.fecha).toLocaleDateString('es-ES') : '-'}
                </td>
                <td className="py-3 text-right pr-4 text-gray-700">
                  {inv.total.toFixed(2)} EUR
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Published Invoices
  const renderPublishedInvoices = () => {
    if (publishedLoading) {
      return <div className="text-slate-500 text-center py-8">Cargando facturas publicadas...</div>;
    }

    const invoices = publishedData?.invoices || [];

    if (invoices.length === 0) {
      return (
        <div className="text-center py-12 text-slate-500">
          <FileText size={48} className="mx-auto mb-4 opacity-50" />
          <p>No hay facturas publicadas este ano</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-slate-500 text-sm border-b border-gray-200">
              <th className="pb-3 pl-4">Factura</th>
              <th className="pb-3">Pedido</th>
              <th className="pb-3">Fecha</th>
              <th className="pb-3">Email</th>
              <th className="pb-3 text-right pr-4">Total</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv: PublishedInvoice, i: number) => (
              <tr key={i} className="border-b border-gray-100 hover:bg-slate-50">
                <td className="py-3 pl-4">
                  <span className="text-gray-900 font-medium">{inv.serie}-{inv.codigo}</span>
                </td>
                <td className="py-3">
                  <span className="text-primary-600">#{inv.order_id}</span>
                </td>
                <td className="py-3 text-slate-500 text-sm">
                  {inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('es-ES') : '-'}
                </td>
                <td className="py-3">
                  {inv.email_sent ? (
                    <span className="text-success-600 text-sm flex items-center gap-1">
                      <MailCheck size={14} />
                      Enviado
                    </span>
                  ) : (
                    <span className="text-slate-400 text-sm">-</span>
                  )}
                </td>
                <td className="py-3 text-right pr-4 text-gray-700">
                  {inv.total.toFixed(2)} EUR
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // History
  const renderHistory = () => {
    if (historyLoading) {
      return <div className="text-slate-500 text-center py-8">Cargando historial...</div>;
    }

    const history = historyData?.history || [];

    if (history.length === 0) {
      return (
        <div className="text-center py-12 text-slate-500">
          <History size={48} className="mx-auto mb-4 opacity-50" />
          <p>No hay historial de sincronizacion</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-slate-500 text-sm border-b border-gray-200">
              <th className="pb-3 pl-4">Fecha</th>
              <th className="pb-3">Accion</th>
              <th className="pb-3">Factura</th>
              <th className="pb-3">Pedido</th>
              <th className="pb-3">Estado</th>
              <th className="pb-3 pr-4">Mensaje</th>
            </tr>
          </thead>
          <tbody>
            {history.map((item: InvoiceSyncHistoryItem) => (
              <tr key={item.id} className="border-b border-gray-100 hover:bg-slate-50">
                <td className="py-3 pl-4 text-slate-500 text-sm">
                  {new Date(item.created_at).toLocaleString('es-ES')}
                </td>
                <td className="py-3 text-gray-700">{item.action}</td>
                <td className="py-3 text-gray-900">
                  {item.serie && item.codigo ? `${item.serie}-${item.codigo}` : '-'}
                </td>
                <td className="py-3">
                  {item.order_id ? <span className="text-primary-600">#{item.order_id}</span> : '-'}
                </td>
                <td className="py-3">
                  {item.status === 'success' ? (
                    <span className="text-success-600 text-sm flex items-center gap-1">
                      <CheckCircle size={14} />
                      OK
                    </span>
                  ) : (
                    <span className="text-danger-600 text-sm flex items-center gap-1">
                      <XCircle size={14} />
                      Error
                    </span>
                  )}
                </td>
                <td className="py-3 pr-4 text-slate-500 text-sm max-w-xs truncate">
                  {item.message}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="text-danger-500" />
            Facturas PDF
          </h1>
          <p className="text-slate-500 mt-1">
            Publicacion de facturas en la web del cliente
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowEmailConfig(true)}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-gray-700 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Settings size={18} />
            Configurar Email
          </button>
        </div>
      </div>

      {/* Info Banner - Automatic Nightly Sync */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info size={20} className="text-primary-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-blue-900 font-medium">
            Sincronizacion automatica
          </p>
          <p className="text-blue-700 text-sm">
            Las facturas de Factusol se sincronizan automaticamente cada noche a las 03:00.
            Sube los PDFs aqui para publicarlos inmediatamente.
          </p>
        </div>
      </div>

      {/* Email Config Modal */}
      <InvoiceEmailConfigModal
        isOpen={showEmailConfig}
        onClose={() => setShowEmailConfig(false)}
      />

      {/* Stats */}
      {renderStats()}

      {/* Upload Section */}
      {renderUploadSection()}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'pending'
              ? 'text-primary-600 border-b-2 border-blue-600'
              : 'text-slate-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <Clock size={16} />
            Pendientes
            {(stats?.pending || 0) > 0 && (
              <span className="bg-yellow-500 text-white text-xs px-2 py-0.5 rounded-full">
                {stats?.pending || 0}
              </span>
            )}
          </div>
        </button>

        <button
          onClick={() => setActiveTab('published')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'published'
              ? 'text-primary-600 border-b-2 border-blue-600'
              : 'text-slate-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <CheckCircle size={16} />
            Publicadas
          </div>
        </button>

        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'history'
              ? 'text-primary-600 border-b-2 border-blue-600'
              : 'text-slate-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <History size={16} />
            Historial
          </div>
        </button>
      </div>

      {/* Content */}
      <div>
        {activeTab === 'pending' && (
          <div className="bg-white rounded-lg border overflow-hidden shadow-sm">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-gray-900 font-medium flex items-center gap-2">
                <Clock size={18} className="text-yellow-600" />
                Facturas sin PDF publicado ({stats?.pending || 0})
              </h2>
              <p className="text-slate-500 text-sm mt-1">
                Facturas del ano {stats?.year || new Date().getFullYear()} que aun no tienen PDF en la web.
              </p>
            </div>
            {renderPendingInvoices()}
          </div>
        )}

        {activeTab === 'published' && (
          <div className="bg-white rounded-lg border overflow-hidden shadow-sm">
            {renderPublishedInvoices()}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="bg-white rounded-lg border overflow-hidden shadow-sm">
            {renderHistory()}
          </div>
        )}
      </div>
    </div>
  );
}
